import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import FullCalendar from '@fullcalendar/react';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateTime } from 'luxon';
import { EventImpl } from '@fullcalendar/core/internal';
import { DUMMY_EVENT_ID } from '../kitchen-capacity.utils';
import {
  convertEventApiToRecurring,
  convertKitchenThrottleSettingsToEventInput,
  convertSetOrderLimitsFormDataToEventSource,
  createOrderLimitsDummyEvent,
  filterOrderLimitsOverlappedEvents,
  recurringEventsToKitchenThrottleConfig,
  SetOrderLimitsFormDataProps,
} from './order-limits.utils';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import { useFormContext } from 'react-hook-form';
import { convertNormalHoursToEventInput } from 'components/location-hours/normal-hours-calendar/normal-hours.utils';
import { generateBackgroundEvents } from 'components/location-hours/temporary-hours-calendar/temporary-hours.utils';
import { StandardHoursType } from 'components/location-hours/temporary-hours-calendar/temporary-hours.hooks';

export type OrderLimitsCalendarType = {
  orderLimitsRef: MutableRefObject<FullCalendar>;
  orderLimitsEvents: EventInput[];
  backgroundEvents: EventInput[];
  addEvents: (setHourFormData: SetOrderLimitsFormDataProps) => void;
  addDummyEvent: (start: DateTime, end: DateTime) => EventInput;
  removeDummyEvent: () => void;
  eventsSet: (events: EventApi[]) => void;

  handleCalendarViewChange: (view: 'list' | 'week') => void;
  convertEventsToOrderLimits: (
    orderLimitsEvents: EventInput[],
  ) => Partial<StoreOrderingForm>;
  orderLimitsCalendarLoaded: boolean;
  standardHours: StandardHoursType;
};

type UseOrderLimitsProps = {
  storeOrderingData: StoreOrderingForm;
};

// Define custom hook for handling order limits
export const useOrderLimits = ({ storeOrderingData }: UseOrderLimitsProps) => {
  const { setValue } = useFormContext();

  const [orderLimitsEvents, setOrderLimitsEvents] = useState<EventInput[]>();
  const [orderLimitsCalendarInitialLoad, setOrderLimitsCalendarInitialLoad] =
    useState(false);
  const { kitchenThrottleConfig, pickupOrderHours, deliveryOrderHours } =
    storeOrderingData;

  const orderLimitsRef = useRef<FullCalendar>();
  const orderLimitsCalendarApi = orderLimitsRef.current?.getApi();

  const initialEvents = useMemo(
    () => convertKitchenThrottleSettingsToEventInput(kitchenThrottleConfig),
    [kitchenThrottleConfig],
  );

  const [isShowingNormalHours, setIsShowingNormalHours] =
    useState<boolean>(false);

  const backgroundEvents = useMemo(() => {
    const locationHoursEvents = [
      ...convertNormalHoursToEventInput(pickupOrderHours, {
        orderType: 'PICKUP',
        eventType: 'LocationHours',
      }),
      ...convertNormalHoursToEventInput(deliveryOrderHours, {
        orderType: 'DELIVERY',
        eventType: 'LocationHours',
      }),
    ];

    return generateBackgroundEvents(locationHoursEvents, isShowingNormalHours);
  }, [pickupOrderHours, deliveryOrderHours, isShowingNormalHours]);

  const convertEventsToOrderLimits = useCallback(
    (orderLimitsEvents: EventInput[]): Partial<StoreOrderingForm> => {
      return {
        kitchenThrottleConfig: {
          ...kitchenThrottleConfig,
          // TODO: This logic will be changed in a future story.
          displayInfo:
            kitchenThrottleConfig?.displayInfo &&
            Object.keys(kitchenThrottleConfig.displayInfo).length > 0
              ? kitchenThrottleConfig.displayInfo
              : {
                  EN: "We're sorry - our kitchen is at capacity and we cannot accept more Orders at the moment! Please try again in 15 minutes.",
                },
          timeslots: recurringEventsToKitchenThrottleConfig(orderLimitsEvents),
        },
      };
    },
    [kitchenThrottleConfig],
  );

  const addDummyEvent = useCallback(
    (start: DateTime, end: DateTime) => {
      const dummyEvent = createOrderLimitsDummyEvent(start, end);
      orderLimitsCalendarApi?.addEvent(dummyEvent);
      return dummyEvent;
    },
    [orderLimitsCalendarApi],
  );

  const removeDummyEvent = useCallback(() => {
    const dummyEvent = orderLimitsCalendarApi?.getEventById(DUMMY_EVENT_ID);
    if (dummyEvent) {
      dummyEvent.remove();
    }
  }, [orderLimitsCalendarApi]);

  const addEvents = useCallback(
    (setHourFormData: SetOrderLimitsFormDataProps) => {
      const newEvents =
        convertSetOrderLimitsFormDataToEventSource(setHourFormData);

      const overlappedEvents = filterOrderLimitsOverlappedEvents(
        setHourFormData,
        orderLimitsEvents,
      );

      // Remove overlapped events from the calendar
      overlappedEvents.forEach(event => {
        orderLimitsCalendarApi?.getEventById(event.id).remove();
      });

      // Remove existing event if updating
      if (setHourFormData.eventId) {
        orderLimitsCalendarApi?.getEventById(setHourFormData.eventId).remove();
      }

      // Add new events to the calendar
      orderLimitsCalendarApi?.addEventSource(newEvents);
    },
    [orderLimitsEvents, orderLimitsCalendarApi],
  );

  const eventsSet = useCallback((events: EventImpl[]) => {
    // Use reduce to create a map with the last occurrence of each event by unique identifier
    const eventMap = events.reduce((map, event) => {
      map.set(event.id, event);
      return map;
    }, new Map());

    // Convert the map values to an array, filter out events with display = "background",
    // map to the required format, and set the unique events
    setOrderLimitsEvents(
      Array.from(eventMap.values())
        ?.filter(event => event.display !== 'background')
        .map(convertEventApiToRecurring),
    );
  }, []);

  const handleCalendarViewChange = useCallback(
    (view: 'list' | 'week') => {
      switch (view) {
        case 'week':
          orderLimitsCalendarApi?.changeView('timeGridWeek');
          break;
        default:
          orderLimitsCalendarApi?.changeView('listWeek');
      }
    },
    [orderLimitsCalendarApi],
  );

  const removeAllBackgroundEvents = useCallback(() => {
    const allEvents = orderLimitsCalendarApi.getEvents();
    allEvents.forEach(event => {
      if (event.extendedProps.eventType === 'LocationHours') {
        event.remove();
      }
    });
  }, [orderLimitsCalendarApi]);

  useEffect(() => {
    if (orderLimitsCalendarApi && !orderLimitsCalendarInitialLoad) {
      orderLimitsCalendarApi.removeAllEvents();
      orderLimitsCalendarApi.addEventSource(initialEvents);
      setOrderLimitsEvents(initialEvents); // Set initial events to orderLimitsEvents state
      setOrderLimitsCalendarInitialLoad(true); // Set initial load flag to true
    }
  }, [initialEvents, orderLimitsCalendarApi, orderLimitsCalendarInitialLoad]);

  useEffect(() => {
    setValue(
      'ordering.kitchenThrottleConfig.timeslots',
      convertEventsToOrderLimits(orderLimitsEvents).kitchenThrottleConfig
        .timeslots,
    );

    setValue(
      'ordering.kitchenThrottleConfig.displayInfo',
      convertEventsToOrderLimits(orderLimitsEvents).kitchenThrottleConfig
        .displayInfo,
    );
  }, [convertEventsToOrderLimits, orderLimitsEvents, setValue]);

  useEffect(() => {
    if (backgroundEvents && orderLimitsCalendarApi) {
      removeAllBackgroundEvents();
      orderLimitsCalendarApi.addEventSource(backgroundEvents);
    }
  }, [backgroundEvents, orderLimitsCalendarApi, removeAllBackgroundEvents]);

  return {
    orderLimitsRef,
    orderLimitsEvents,
    backgroundEvents,
    addEvents,
    eventsSet,
    addDummyEvent,
    removeDummyEvent,
    handleCalendarViewChange,
    convertEventsToOrderLimits,
    orderLimitsCalendarLoaded: orderLimitsCalendarInitialLoad,
    standardHours: {
      isShowingNormalHours,
      setIsShowingNormalHours,
    },
  };
};
